import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import Field from "../components/Field";
import PrimaryButton from "../components/PrimaryButton";
import FeatureCarousel from "../components/FeatureCarousel";
import { featureSlides } from "../data/featureSlides";
import { loginUser } from "../api/authService";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Login() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      toast.warn("Please enter email and password", { position: "top-center", autoClose: 1400 });
      return;
    }
    setLoading(true);
    try {
      const data = await loginUser(email, password);
      toast.dismiss();
      toast.success("Welcome back!", { position: "top-center", autoClose: 1200 });
      await refreshAuth();
      navigate("/dashboard");
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Login failed", { position: "top-center", autoClose: 2600 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-stretch lg:items-center justify-center overflow-hidden">
      {}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-emerald-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors duration-300" />
      <div className="pointer-events-none absolute inset-0 opacity-[.04] dark:opacity-[.08] [background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22><path fill=%22%23a1a1aa%22 fill-opacity=%220.5%22 d=%22M0 39h40v1H0zM39 0v40h1V0z%22/></svg>')]" />

      {}
      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 max-w-[1100px] w-full px-4 sm:px-6 lg:px-10 py-8 sm:py-12 lg:py-0">
        {}
        <div className="col-span-1 lg:col-span-2 flex items-center justify-center sm:justify-between mb-4 sm:mb-6">
          <Logo />
        </div>

        {}
        <form
          onSubmit={submit}
          className="order-1 w-full max-w-md bg-white/80 dark:bg-slate-800/90 backdrop-blur-md rounded-3xl
                     ring-1 ring-black/5 dark:ring-white/10 shadow-[0_20px_60px_-20px_rgba(2,6,23,0.25)] dark:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]
                     p-6 sm:p-8 md:p-10 space-y-6 sm:space-y-8 mx-auto transition-colors"
          aria-label="Login form"
        >
          <header className="space-y-2 text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">Welcome Back</h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">
              Sign in to manage bookings, assign drivers, and keep every ride on schedule.
            </p>
          </header>

          <div className="space-y-4">
            <Field
              placeholder="Email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Field
              placeholder="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <PrimaryButton
            type="submit"
            disabled={loading}
            aria-label="Login"
          >
            {loading ? "Signing you in..." : "Login"}
          </PrimaryButton>

          <div className="text-sm text-center">
            <Link to="/forgot-password" className="text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
              Forgot Password?
            </Link>
          </div>
        </form>

        {}
        <div className="order-2 flex items-center justify-center">
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-3xl ring-1 ring-black/5 dark:ring-white/10 shadow-xl p-4 sm:p-6 transition-colors">
            <FeatureCarousel slides={featureSlides} />
          </div>
        </div>
      </div>
    </div>
  );
}
